import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Printer,
  Mail,
  Calendar,
  Building2,
  User,
  Phone,
  MapPin,
  Check
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface QuoteData {
  quoteNumber: string;
  date: string;
  validityDays: number;
  client: {
    companyName: string;
    contactName: string;
    email: string;
    phone: string;
    address: string;
    siret?: string;
  };
  packs: {
    type: 'essentiel' | 'premium' | 'signature';
    quantity: number;
    customName?: string;
  }[];
  auditComplet?: {
    included: boolean;
    offered: boolean; // Si offert, prix = 0
  };
  discount?: number;
  notes?: string;
}

const PACKS = {
  essentiel: {
    name: 'Pack Essentiel',
    price: 1000,
    description: 'Forfait de base SEO & GEO',
    features: [
      'Optimisation SEO technique',
      'Gestion Google Business Profile',
      'Audit mensuel',
      'Reporting mensuel',
      'Support email (48h)',
      'Couverture 2 langues (FR + 1)',
      'Réunion stratégique mensuelle',
    ]
  },
  premium: {
    name: 'Pack Premium (6 mois)',
    price: 2000,
    description: 'Accompagnement SEO & GEO complet avec résultats mesurables',
    features: [
      'Audit technique complet + corrections prioritaires',
      'Reporting mensuel détaillé avec KPIs et recommandations',
      'Recherche de 100+ mots-clés à fort potentiel commercial',
      'Optimisation internationale (hreflang) pour 3 langues',
      'Création de 2 landing pages SEO optimisées par mois (traduites)',
      'Gestion complète Google Business Profile (posts, photos, avis)',
      'Consulting stratégique 2h par mois avec expert dédié',
      'Réunions bi-mensuelles (mois 1-2), puis mensuelles',
      'Veille concurrentielle et analyse opportunités de marché',
      'Amélioration classement recommandations IA (ChatGPT, Gemini)',
      'Plateforme STRATOS : dashboard temps réel et analytics avancés',
      'Support prioritaire avec réponse sous 24h',
    ]
  },
  signature: {
    name: 'Pack Signature',
    price: 3500,
    description: 'Service VIP tout inclus',
    features: [
      'Tout le Pack Premium',
      'Stratégie marketing digitale complète',
      'Création de contenu premium',
      'Netlinking & Relations Presse',
      'Consulting stratégique (4h/mois)',
      'Réunions hebdomadaires sur demande',
      'Couverture 4 langues (FR + 3)',
      'Support VIP 7j/7',
      'Accès API & Intégrations',
      'Accompagnement personnalisé',
    ]
  }
};

const AUDIT_COMPLET = {
  name: 'Audit SEO Complet Initial',
  price: 4000,
  description: 'Audit approfondi technique, éditorial et concurrentiel',
  features: [
    'Crawl technique complet (Screaming Frog)',
    'Audit on-page approfondi',
    'Analyse concurrentielle détaillée',
    'Recherche de mots-clés (200+ KW)',
    'Audit de contenu existant',
    'Analyse backlinks',
    'Audit vitesse et Core Web Vitals',
    'Recommandations priorisées',
    'Roadmap SEO 6 mois',
    'Présentation en visio (2h)',
  ]
};

interface QuoteGeneratorProps {
  initialData?: Partial<QuoteData['client']>;
}

export const QuoteGenerator: React.FC<QuoteGeneratorProps> = ({ initialData }) => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [quoteData, setQuoteData] = useState<QuoteData>({
    quoteNumber: `DEVIS-${Date.now().toString().slice(-6)}`,
    date: new Date().toISOString().split('T')[0],
    validityDays: 30,
    client: {
      companyName: initialData?.companyName || '',
      contactName: initialData?.contactName || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      address: initialData?.address || '',
      siret: initialData?.siret || ''
    },
    packs: [{ type: 'premium', quantity: 1 }],
    auditComplet: {
      included: false,
      offered: false
    },
    discount: 0,
    notes: ''
  });

  const addPack = () => {
    setQuoteData(prev => ({
      ...prev,
      packs: [...prev.packs, { type: 'essentiel', quantity: 1 }]
    }));
  };

  const removePack = (index: number) => {
    setQuoteData(prev => ({
      ...prev,
      packs: prev.packs.filter((_, i) => i !== index)
    }));
  };

  const updatePack = (index: number, field: string, value: any) => {
    setQuoteData(prev => ({
      ...prev,
      packs: prev.packs.map((pack, i) => 
        i === index ? { ...pack, [field]: value } : pack
      )
    }));
  };

  const calculateTotal = () => {
    const packsSubtotal = quoteData.packs.reduce((sum, pack) => {
      return sum + (PACKS[pack.type].price * pack.quantity);
    }, 0);
    
    // Ajouter l'audit complet si inclus et non offert
    const auditPrice = quoteData.auditComplet?.included && !quoteData.auditComplet?.offered 
      ? AUDIT_COMPLET.price 
      : 0;
    
    const subtotal = packsSubtotal + auditPrice;
    const discountAmount = (subtotal * (quoteData.discount || 0)) / 100;
    const totalHT = subtotal - discountAmount;
    const tva = totalHT * 0.2;
    const totalTTC = totalHT + tva;
    
    return { subtotal, discountAmount, totalHT, tva, totalTTC, auditPrice };
  };

  const { subtotal, discountAmount, totalHT, tva, totalTTC, auditPrice } = calculateTotal();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!documentRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = documentRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 0;
      const totalPages = Math.ceil(imgHeight / pdfHeight);

      while (heightLeft > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        // Ajouter numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNumber + 1} / ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      const fileName = quoteData.client.companyName 
        ? `Devis_STRATOS_${quoteData.client.companyName.replace(/\s+/g, '_')}_${quoteData.quoteNumber}.pdf`
        : `Devis_STRATOS_${quoteData.quoteNumber}.pdf`;
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Form Section */}
      <Card className="p-6 print:hidden">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Générateur de Devis
        </h2>
        
        <div className="space-y-6">
          {/* Client Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Informations Client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison sociale *
                </label>
                <input
                  type="text"
                  value={quoteData.client.companyName}
                  onChange={(e) => setQuoteData(prev => ({
                    ...prev,
                    client: { ...prev.client, companyName: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold focus:border-transparent"
                  placeholder="Nom de l'établissement"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact *
                </label>
                <input
                  type="text"
                  value={quoteData.client.contactName}
                  onChange={(e) => setQuoteData(prev => ({
                    ...prev,
                    client: { ...prev.client, contactName: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold focus:border-transparent"
                  placeholder="Nom du contact"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={quoteData.client.email}
                  onChange={(e) => setQuoteData(prev => ({
                    ...prev,
                    client: { ...prev.client, email: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold focus:border-transparent"
                  placeholder="email@exemple.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={quoteData.client.phone}
                  onChange={(e) => setQuoteData(prev => ({
                    ...prev,
                    client: { ...prev.client, phone: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold focus:border-transparent"
                  placeholder="+33 1 23 45 67 89"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={quoteData.client.address}
                  onChange={(e) => setQuoteData(prev => ({
                    ...prev,
                    client: { ...prev.client, address: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold focus:border-transparent"
                  placeholder="Adresse complète"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SIRET
                </label>
                <input
                  type="text"
                  value={quoteData.client.siret}
                  onChange={(e) => setQuoteData(prev => ({
                    ...prev,
                    client: { ...prev.client, siret: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold focus:border-transparent"
                  placeholder="SIRET (optionnel)"
                />
              </div>
            </div>
          </div>

          {/* Packs Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Prestations
            </h3>
            <div className="space-y-3">
              {quoteData.packs.map((pack, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <select
                    value={pack.type}
                    onChange={(e) => updatePack(index, 'type', e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
                  >
                    <option value="essentiel">Pack Essentiel - 1 000€/mois</option>
                    <option value="premium">Pack Premium (6 mois) - 2 000€/mois</option>
                    <option value="signature">Pack Signature - 3 500€/mois</option>
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={pack.quantity}
                    onChange={(e) => updatePack(index, 'quantity', parseInt(e.target.value))}
                    className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
                  />
                  {quoteData.packs.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removePack(index)}
                    >
                      Retirer
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" onClick={addPack}>
                + Ajouter un pack
              </Button>
            </div>
          </div>

          {/* Audit Complet */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Audit Complet Initial (optionnel)
            </h3>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">
                    {AUDIT_COMPLET.name} - {AUDIT_COMPLET.price.toLocaleString()}€ HT
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    {AUDIT_COMPLET.description}
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {AUDIT_COMPLET.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quoteData.auditComplet?.included || false}
                    onChange={(e) => setQuoteData(prev => ({
                      ...prev,
                      auditComplet: {
                        included: e.target.checked,
                        offered: e.target.checked ? (prev.auditComplet?.offered || false) : false
                      }
                    }))}
                    className="w-4 h-4 text-stratos-gold rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Inclure l'audit complet dans le devis
                  </span>
                </label>
                {quoteData.auditComplet?.included && (
                  <label className="flex items-center gap-2 cursor-pointer ml-6">
                    <input
                      type="checkbox"
                      checked={quoteData.auditComplet?.offered || false}
                      onChange={(e) => setQuoteData(prev => ({
                        ...prev,
                        auditComplet: {
                          ...prev.auditComplet!,
                          offered: e.target.checked
                        }
                      }))}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <span className="text-sm font-medium text-green-700">
                      🎁 Offrir l'audit complet (valeur 4 000€)
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Discount & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remise (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={quoteData.discount}
                onChange={(e) => setQuoteData(prev => ({
                  ...prev,
                  discount: parseFloat(e.target.value) || 0
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Validité (jours)
              </label>
              <input
                type="number"
                min="1"
                value={quoteData.validityDays}
                onChange={(e) => setQuoteData(prev => ({
                  ...prev,
                  validityDays: parseInt(e.target.value)
                }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes / Conditions particulières
            </label>
            <textarea
              value={quoteData.notes}
              onChange={(e) => setQuoteData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
              placeholder="Informations complémentaires..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} disabled={isGenerating}>
              <Download className="w-4 h-4 mr-2" />
              {isGenerating ? 'Génération...' : 'Télécharger PDF'}
            </Button>
            <Button variant="outline">
              <Mail className="w-4 h-4 mr-2" />
              Envoyer par email
            </Button>
          </div>
          {isGenerating && (
            <p className="text-sm text-blue-600 mt-3 animate-pulse">
              ⏳ Génération du PDF en cours... Veuillez patienter.
            </p>
          )}
        </div>
      </Card>

      {/* Preview / Print Section */}
      <div ref={documentRef} className="print:block">
        <Card className="p-12 bg-white print:shadow-none print:border-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-12 pb-8 border-b-2 border-stratos-gold">
            <div>
              <h1 
                className="text-5xl font-bold mb-2"
                style={{
                  background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                STRATOS
              </h1>
              <p className="text-xl text-gray-600 font-light">Hospitality</p>
              <p className="text-sm text-gray-500 mt-4">by Getup Agency</p>
            </div>
            <div className="text-right">
              <div className="mb-6">
                <Badge variant="default" className="text-lg px-4 py-2">
                  DEVIS
                </Badge>
              </div>
              <p className="text-sm text-gray-600">
                <strong>N° {quoteData.quoteNumber}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Date : {new Date(quoteData.date).toLocaleDateString('fr-FR')}
              </p>
              <p className="text-sm text-gray-600">
                Validité : {quoteData.validityDays} jours
              </p>
            </div>
          </div>

          {/* Company & Client Info */}
          <div className="grid grid-cols-2 gap-8 mb-12">
            {/* Stratos Info */}
            <div>
              <h3 className="text-sm font-bold text-stratos-gold uppercase mb-3">
                Prestataire
              </h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p className="font-semibold text-gray-900">STRATOS Hospitality</p>
                <p>by Getup Agency</p>
                <p>SARL au capital de 150 500€</p>
                <p>SIRET : 822 096 913 00022</p>
                <p>TVA : FR82822096913</p>
                <p className="pt-2">Représentée par Adrien Cerdan</p>
              </div>
            </div>

            {/* Client Info */}
            <div>
              <h3 className="text-sm font-bold text-stratos-gold uppercase mb-3">
                Client
              </h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p className="font-semibold text-gray-900">
                  {quoteData.client.companyName || '[Raison sociale]'}
                </p>
                {quoteData.client.contactName && (
                  <p>Contact : {quoteData.client.contactName}</p>
                )}
                {quoteData.client.address && <p>{quoteData.client.address}</p>}
                {quoteData.client.siret && <p>SIRET : {quoteData.client.siret}</p>}
                {quoteData.client.email && (
                  <p className="pt-2">
                    <Mail className="w-3 h-3 inline mr-1" />
                    {quoteData.client.email}
                  </p>
                )}
                {quoteData.client.phone && (
                  <p>
                    <Phone className="w-3 h-3 inline mr-1" />
                    {quoteData.client.phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Services Table */}
          <div className="mb-8 print:break-inside-avoid">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Prestations proposées
            </h3>
            <table className="w-full print:break-inside-avoid">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-stratos-gold">
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">
                    Désignation
                  </th>
                  <th className="text-center p-3 text-sm font-semibold text-gray-700 w-24">
                    Qté
                  </th>
                  <th className="text-right p-3 text-sm font-semibold text-gray-700 w-32">
                    Prix unit. HT
                  </th>
                  <th className="text-right p-3 text-sm font-semibold text-gray-700 w-32">
                    Total HT
                  </th>
                </tr>
              </thead>
              <tbody>
                {quoteData.packs.map((pack, index) => {
                  const packInfo = PACKS[pack.type];
                  return (
                    <React.Fragment key={index}>
                      <tr className="border-b">
                        <td className="p-3">
                          <p className="font-semibold text-gray-900">
                            {pack.customName || packInfo.name}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {packInfo.description}
                          </p>
                          <ul className="mt-2 space-y-1">
                            {packInfo.features.map((feature, i) => (
                              <li key={i} className="text-xs text-gray-600 flex items-center gap-1">
                                <Check className="w-3 h-3 text-green-600" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td className="p-3 text-center text-gray-900">
                          {pack.quantity}
                        </td>
                        <td className="p-3 text-right text-gray-900">
                          {packInfo.price.toLocaleString()}€
                        </td>
                        <td className="p-3 text-right font-semibold text-gray-900">
                          {(packInfo.price * pack.quantity).toLocaleString()}€
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                
                {/* Audit Complet Row */}
                {quoteData.auditComplet?.included && (
                  <tr className="border-b bg-blue-50">
                    <td className="p-3">
                      <p className="font-semibold text-gray-900">
                        {AUDIT_COMPLET.name}
                        {quoteData.auditComplet.offered && (
                          <Badge variant="success" className="ml-2">OFFERT</Badge>
                        )}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {AUDIT_COMPLET.description}
                      </p>
                      <ul className="mt-2 space-y-1">
                        {AUDIT_COMPLET.features.map((feature, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-600" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {quoteData.auditComplet.offered && (
                        <p className="text-xs text-green-700 font-semibold mt-2 italic">
                          Offert par Stratos Hospitality (valeur commerciale : 4 000€)
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-center text-gray-900">1</td>
                    <td className="p-3 text-right text-gray-900">
                      {quoteData.auditComplet.offered ? (
                        <span className="text-green-600 line-through">
                          {AUDIT_COMPLET.price.toLocaleString()}€
                        </span>
                      ) : (
                        `${AUDIT_COMPLET.price.toLocaleString()}€`
                      )}
                    </td>
                    <td className="p-3 text-right font-semibold">
                      {quoteData.auditComplet.offered ? (
                        <span className="text-green-600">OFFERT</span>
                      ) : (
                        <span className="text-gray-900">{AUDIT_COMPLET.price.toLocaleString()}€</span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8 print:break-inside-avoid">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-sm py-2">
                <span className="text-gray-600">Sous-total HT :</span>
                <span className="font-semibold text-gray-900">
                  {subtotal.toLocaleString()}€
                </span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm py-2 text-green-600">
                  <span>Remise ({quoteData.discount}%) :</span>
                  <span className="font-semibold">
                    -{discountAmount.toLocaleString()}€
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm py-2 border-t">
                <span className="text-gray-600">Total HT :</span>
                <span className="font-semibold text-gray-900">
                  {totalHT.toLocaleString()}€
                </span>
              </div>
              <div className="flex justify-between text-sm py-2">
                <span className="text-gray-600">TVA (20%) :</span>
                <span className="font-semibold text-gray-900">
                  {tva.toLocaleString()}€
                </span>
              </div>
              <div className="flex justify-between text-lg py-3 border-t-2 border-stratos-gold">
                <span className="font-bold text-gray-900">Total TTC :</span>
                <span className="font-bold text-stratos-gold">
                  {totalTTC.toLocaleString()}€
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {quoteData.notes && (
            <div className="mb-8 p-4 bg-gray-50 rounded-lg print:break-inside-avoid">
              <h3 className="text-sm font-bold text-gray-900 mb-2">
                Notes & Conditions particulières
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {quoteData.notes}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="pt-8 border-t space-y-4 text-xs text-gray-600 print:break-inside-avoid">
            <p>
              <strong>Modalités de règlement :</strong> Paiement mensuel par virement bancaire SEPA. 
              Première facturation à la signature du contrat.
            </p>
            <p>
              <strong>Engagement :</strong> Durée minimale de 6 mois. Pas de résiliation possible pendant 
              la période d'engagement. À l'issue des 6 premiers mois, le client décide de poursuivre pour 
              6 mois supplémentaires.
            </p>
            <p>
              <strong>Acceptation :</strong> Ce devis est valable {quoteData.validityDays} jours à compter de sa date d'émission. 
              Signature précédée de la mention « Bon pour accord ».
            </p>
            
            {/* Spacer pour forcer la signature sur page 2 */}
            <div className="h-48"></div>
            
            <div className="mt-8 pt-8 border-t">
              <div className="max-w-md">
                <p className="font-semibold mb-4">Signature du Client</p>
                <p className="text-xs mb-4">(Précédée de la mention « Bon pour accord »)</p>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-gray-600 mb-1">Nom et qualité du signataire :</p>
                    <div className="border-b border-gray-400 pb-6 mb-4"></div>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Date :</p>
                    <div className="border-b border-gray-400 pb-6 mb-4"></div>
                  </div>
                </div>
                <p className="text-gray-600 mb-2">Signature et cachet de l'entreprise :</p>
                <div className="border-2 border-gray-300 rounded-lg h-32"></div>
              </div>
            </div>
          </div>
          
          {/* Pied de page */}
          <div className="mt-12 pt-6 border-t text-center text-xs text-gray-500">
            <p className="mb-1">Devis émis par <strong>Stratos Hospitality</strong> - SARL au capital de 150 500€</p>
            <p>SIRET : 822 096 913 00022</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

